import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, WifiOff, QrCode } from "lucide-react";
import { AppStoreButton, GooglePlayButton } from "@/components/landing/StoreButtons";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];
const ICONS = { b1: ShieldCheck, b2: BadgeCheck, b3: WifiOff };
const KEYS = ["b1", "b2", "b3"];

const QRPlaceholder = () => (
  <svg viewBox="0 0 64 64" className="w-full h-full" aria-hidden="true">
    <rect width="64" height="64" fill="#07090B" />
    <rect x="4" y="4" width="14" height="14" stroke="#39FF14" strokeWidth="2" fill="none" />
    <rect x="8" y="8" width="6" height="6" fill="#39FF14" />
    <rect x="46" y="4" width="14" height="14" stroke="#39FF14" strokeWidth="2" fill="none" />
    <rect x="50" y="8" width="6" height="6" fill="#39FF14" />
    <rect x="4" y="46" width="14" height="14" stroke="#39FF14" strokeWidth="2" fill="none" />
    <rect x="8" y="50" width="6" height="6" fill="#39FF14" />
    {Array.from({ length: 12 }).map((_, r) =>
      Array.from({ length: 12 }).map((__, c) => {
        const x = 22 + c * 3, y = 22 + r * 3;
        const seed = (r * 7 + c * 13) % 11;
        if (seed > 6) return null;
        return <rect key={`${r}-${c}`} x={x} y={y} width="2.2" height="2.2" fill="#EAF0F6" opacity="0.85" />;
      })
    )}
  </svg>
);

export const Download = () => {
  const { t } = useLanguage();
  return (
    <section id="download" data-testid="download-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 hero-backdrop pointer-events-none" />
      <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-[80%] h-80 section-edge-glow pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ y: 22, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease }}
          className="relative rounded-[28px] border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-10 lg:p-14 shadow-[0_30px_90px_rgba(0,0,0,0.55)] overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#39FF14]/12 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[#7DD3FC]/8 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#39FF14]/90">{t.download.eyebrow}</span>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-3 tracking-tight">
                {t.download.title1}<br />
                <span className="text-[#39FF14] drop-shadow-[0_0_24px_rgba(57,255,20,0.4)]">{t.download.title2}</span>
              </h2>
              <p className="mt-4 text-white/70 text-base sm:text-lg leading-relaxed max-w-lg">{t.download.subhead}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <AppStoreButton variant="primary" testId="download-primary-cta-button" />
                <GooglePlayButton variant="glass" testId="download-play-button" />
              </div>
              <div data-testid="download-trust-badges" className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {KEYS.map((k) => {
                  const Icon = ICONS[k];
                  return (
                    <div key={k} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#39FF14]/10 border border-[#39FF14]/30 text-[#39FF14]">
                        <Icon className="w-4 h-4" />
                      </span>
                      <div>
                        <div className="font-display font-semibold text-white text-sm">{t.download[k].title}</div>
                        <div className="text-xs text-white/55 mt-0.5">{t.download[k].desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-5 flex flex-col items-center lg:items-end">
              <div data-testid="download-qr-code" className="relative w-full max-w-[320px] rounded-[24px] border border-white/12 bg-black/40 backdrop-blur-xl p-5 shadow-[0_30px_90px_rgba(0,0,0,0.55)]">
                <div className="absolute -inset-[1px] rounded-[24px] pointer-events-none" style={{ boxShadow: "0 0 0 1px rgba(57,255,20,0.18), 0 0 28px rgba(57,255,20,0.10)" }} />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <QrCode className="w-4 h-4 text-[#39FF14]" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/75">{t.download.scan}</span>
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/45">v3.2</span>
                </div>
                <div className="mt-4 aspect-square rounded-[18px] border border-white/10 bg-[#07090B] p-3">
                  <QRPlaceholder />
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display font-semibold text-white text-sm">MotoGO</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#39FF14]">{t.download.free}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
